/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable @typescript-eslint/ban-ts-ignore */
import React, { useContext, useEffect } from "react"

import { useApiContext, useApiContextRequest } from "~/api"
import { ApiContextFetchListUrl, ApiContextFetchOptionsListType, ApiContextType } from "~/api/types"
import { useApiFetch } from "~/helpers/useApiFetch"
import { useSetState } from "~/helpers/useSetState"

type ApiQuery<T extends ApiContextFetchListUrl> = ApiContextFetchOptionsListType[T]

type Props<T extends ApiContextFetchListUrl> = {
  url: T
  children(props: {
    filterState: ApiQuery<T>
    updateFilterState: (state: ApiQuery<T>) => void
    isLoading: boolean
    items: ApiContextType[T]["items"]
  }): JSX.Element
}

type State<T extends ApiContextFetchListUrl> = {
  filterState: ApiQuery<T>
  items: ApiContextType[T]["items"]
  touched: boolean
}

type FilterContextType = {
  // filterState: ApiQuery<ApiContextFetchListUrl>
  filterState: any | undefined
  totalPages: number
  isLoading: boolean
  updateFilterState: (state: ApiQuery<ApiContextFetchListUrl>) => void
}

const FilterContext = React.createContext<FilterContextType>({
  filterState: {},
  totalPages: 1,
  isLoading: false,
  updateFilterState: () => null,
})

const setUrlParams = (query: Record<string, any>): void => {
  const urlQuery = Object.keys(query)
    .filter((key) => key !== "page_number")
    .filter((key) => query[key] && query[key].length !== 0)
    .map((key) => [key, query[key]])

  const urlParams = new URLSearchParams(urlQuery as any)
  const pathname = location.pathname

  if (urlQuery.length === 0) {
    history.replaceState(null, "", pathname)

    return
  }

  history.replaceState(null, "", `${pathname}?${urlParams.toString()}`)
}

export const FilterWrapper = <T extends ApiContextFetchListUrl>({
  children,
  url,
}: Props<T>): React.ReactElement => {
  const initialData = useApiContext(url)
  const initialRequest = useApiContextRequest(url)
  const {
    code,
    query: { items_per_page, ...query },
  } = initialRequest

  const [{ filterState, touched, items }, setState] = useSetState<State<T>>({
    filterState: { ...query, page_number: 1 },
    items: initialData?.items ?? [],
    touched: false,
  })

  const [data, isLoading] = useApiFetch(
    { url, query: { items_per_page, code, ...filterState } },
    // todo
    undefined,
    [touched],
  )

  const updateFilterState = (newState: ApiQuery<T>): void => {
    const newQuery = {
      ...filterState,
      ...newState,
    }

    setUrlParams(newQuery)
    setState({
      filterState: { ...newQuery, page_number: newState.page_number || 1 },
      touched: true,
    })
  }

  useEffect(() => {
    if (isLoading || data === undefined) {
      return
    }

    if (filterState.page_number !== 1) {
      // @ts-ignore
      setState({ items: [...items, ...data.items] })
    } else {
      setState({ items: data.items ?? [] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading])

  return (
    <FilterContext.Provider
      value={{
        filterState,
        updateFilterState,
        isLoading,
        totalPages: touched && data ? data.totalPages : initialData.totalPages,
      }}
    >
      {children({
        filterState,
        updateFilterState,
        isLoading: isLoading && filterState.page_number === 1,
        items,
      })}
    </FilterContext.Provider>
  )
}

export const useFilterContext = (): FilterContextType => {
  return useContext(FilterContext)
}
